import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Clapperboard, Eye, CircleCheck, FilePen, Pencil, Play, Plus, Trash2, Github, Globe } from 'lucide-react';
import {
  getAllVideos,
  createVideo,
  updateVideo,
  deleteVideo,
  getYouTubeVideoId,
  getYouTubeThumbnail,
  getVideoProductOptions,
  formatDuration,
  formatTimestamp,
  parseChapters,
  chaptersToText,
  toLines,
  LEGACY_UNKNOWN_DURATION,
  VIDEO_LEVELS,
} from '../../../services/videoService';
import { Modal, Popconfirm, Table, TagsInput, toast, Form, Field, useForm } from '../../../ui';
import {
  PageHeader,
  StatCard,
  FilterBar,
  Thumb,
  LoadingState,
  ErrorState,
  useDebouncedValue,
} from '../components';
import EmptyState from '../../../components/site/EmptyState';
import { formatDate, formatINR } from '../../../utils/format';
import { getErrorMessage } from '../../../utils/apiError';
import { formatNumber, isValidURL } from '../utils/helpers';

const durationLabel = seconds => formatDuration(seconds) || '—';

// The server treats the search term as a regular expression.
const escapeRegex = text => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const optionalUrlRule = message => ({
  validator: (_, value) =>
    !value || !String(value).trim() || isValidURL(String(value).trim())
      ? Promise.resolve()
      : Promise.reject(new Error(message)),
});

const EMPTY_VALUES = {
  title: '',
  description: '',
  productId: '',
  videoUrl: '',
  thumbnailUrl: '',
  category: '',
  level: '',
  features: '',
  whatsIncluded: '',
  chapters: '',
  technologyStack: [],
  projectLink: '',
  githubLink: '',
  duration: null,
  isPublished: true,
};

const toFormValues = video => ({
  title: video.title || '',
  description: video.description || '',
  productId: video.productId || '',
  videoUrl: video.videoUrl || '',
  // An automatic YouTube thumbnail shows as blank, so it keeps following the video URL.
  thumbnailUrl: video.thumbnailUrl && video.thumbnailUrl !== getYouTubeThumbnail(video.videoUrl) ? video.thumbnailUrl : '',
  category: video.category || '',
  level: video.level || '',
  features: (video.features || []).join('\n'),
  whatsIncluded: (video.whatsIncluded || []).join('\n'),
  chapters: chaptersToText(video.chapters),
  technologyStack: video.technologyStack || [],
  projectLink: video.projectLink || '',
  githubLink: video.githubLink || '',
  duration: video.duration > 0 && video.duration < LEGACY_UNKNOWN_DURATION ? video.duration : null,
  isPublished: video.isPublished ?? true,
});

const chaptersRule = {
  validator: (_, value) =>
    parseChapters(value)
      ? Promise.resolve()
      : Promise.reject(new Error('Start each line with a time, e.g. "1:20 Admin dashboard"')),
};

/** Add / edit form, mounted fresh for each open. */
const VideoForm = ({ video, onCancel, onSaved }) => {
  const form = useForm();
  const [saving, setSaving] = useState(false);
  const [products, setProducts] = useState({ status: 'loading', items: [] });
  const initialValues = useMemo(() => (video ? toFormValues(video) : EMPTY_VALUES), [video]);

  useEffect(() => {
    let active = true;
    getVideoProductOptions()
      .then(items => active && setProducts({ status: 'ready', items }))
      .catch(() => active && setProducts({ status: 'error', items: [] }));
    return () => {
      active = false;
    };
  }, []);

  const videoUrl = form.getFieldValue('videoUrl') || '';
  const customThumb = (form.getFieldValue('thumbnailUrl') || '').trim();
  const autoThumb = getYouTubeThumbnail(videoUrl);
  const previewThumb = customThumb && isValidURL(customThumb) ? customThumb : autoThumb;
  const duration = form.getFieldValue('duration');
  const productId = form.getFieldValue('productId') || '';
  const linkedProduct = products.items.find(p => p.id === productId);

  let productHelp = 'Shows the price and a Buy now button next to the video.';
  if (products.status === 'error') productHelp = "Couldn't load projects. You can still save the other fields.";
  else if (linkedProduct && !linkedProduct.isPublic)
    productHelp = "This project isn't public yet, so visitors won't see a price or Buy button until it is.";

  // Picking a project fills the fields that are still empty from it.
  const fillFromProduct = id => {
    const product = products.items.find(p => p.id === id);
    if (!product) return;
    const current = form.getFieldsValue();
    const patch = {};
    if (!current.title?.trim()) patch.title = `${product.name} – full demo`;
    if (!current.category?.trim() && product.category) patch.category = product.category;
    if (!current.technologyStack?.length && product.technologies.length) patch.technologyStack = product.technologies;
    if (!toLines(current.features).length && product.features.length) patch.features = product.features.join('\n');
    if (!toLines(current.whatsIncluded).length && product.whatInsideTheBox.length)
      patch.whatsIncluded = product.whatInsideTheBox.join('\n');
    if (Object.keys(patch).length) {
      form.setFieldsValue(patch);
      toast.info('Filled the empty fields from the project. Edit them as you like.');
    }
  };

  const handleValuesChange = changed => {
    if (changed.productId) fillFromProduct(changed.productId);
  };

  const handleFinish = async values => {
    const link = value => (value || '').trim();
    const thumbnail = link(values.thumbnailUrl) || getYouTubeThumbnail(values.videoUrl);
    const seconds = Number(values.duration);

    const payload = {
      title: values.title.trim(),
      description: values.description.trim(),
      videoUrl: values.videoUrl.trim(),
      thumbnailUrl: thumbnail || null,
      technologyStack: values.technologyStack || [],
      // On edit, an empty string removes a link that was there; null leaves the field alone.
      projectLink: link(values.projectLink) || (video?.projectLink ? '' : null),
      githubLink: link(values.githubLink) || (video?.githubLink ? '' : null),
      // An empty string clears these.
      productId: values.productId || '',
      category: link(values.category),
      level: values.level || '',
      features: toLines(values.features),
      whatsIncluded: toLines(values.whatsIncluded),
      chapters: parseChapters(values.chapters) || [],
      duration: Number.isFinite(seconds) && seconds > 0 ? Math.round(seconds) : 0,
      isPublished: Boolean(values.isPublished),
    };

    setSaving(true);
    try {
      if (video) {
        await updateVideo(video.id, payload);
        toast.success('Video updated');
      } else {
        await createVideo(payload);
        toast.success('Video added');
      }
      onSaved();
    } catch (error) {
      toast.error(getErrorMessage(error, "Couldn't save the video. Please try again."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Form form={form} initialValues={initialValues} onFinish={handleFinish} onValuesChange={handleValuesChange}>
      <Field
        name="productId"
        label={
          <>
            Project this video demos <span className="pk-optional">(optional)</span>
          </>
        }
        help={productHelp}
      >
        <select className="pk-select" disabled={products.status === 'loading'}>
          <option value="">{products.status === 'loading' ? 'Loading projects…' : 'Not linked to a project'}</option>
          {productId && !linkedProduct && products.status === 'ready' && (
            <option value={productId}>Project not found ({productId})</option>
          )}
          {products.items.map(p => (
            <option key={p.id} value={p.id}>
              {p.name} · {formatINR(p.price)}
              {p.isPublic ? '' : ' (not public)'}
            </option>
          ))}
        </select>
      </Field>

      <Field
        name="title"
        label="Title"
        rules={[
          { required: true, whitespace: true, message: 'Enter a title' },
          { min: 3, message: 'Use at least 3 characters' },
        ]}
      >
        <input className="pk-input" maxLength={150} />
      </Field>

      <Field
        name="description"
        label="Description"
        rules={[
          { required: true, whitespace: true, message: 'Enter a description' },
          { max: 1000, message: 'Keep it under 1000 characters' },
        ]}
      >
        <textarea className="pk-textarea" rows={3} maxLength={1000} />
      </Field>

      <div className="pk-form__row">
        <Field
          name="videoUrl"
          label="YouTube URL"
          rules={[
            { required: true, whitespace: true, message: 'Paste the YouTube link' },
            {
              validator: (_, value) =>
                !value || getYouTubeVideoId(value)
                  ? Promise.resolve()
                  : Promise.reject(new Error('Use a YouTube link, e.g. https://www.youtube.com/watch?v=…')),
            },
          ]}
        >
          <input className="pk-input" type="url" inputMode="url" placeholder="https://www.youtube.com/watch?v=" />
        </Field>
        <Field
          name="thumbnailUrl"
          label={
            <>
              Thumbnail URL <span className="pk-optional">(optional)</span>
            </>
          }
          rules={[optionalUrlRule('Enter a full image URL that starts with https://')]}
          help="Leave blank to use the YouTube thumbnail."
        >
          <input className="pk-input" type="url" inputMode="url" placeholder="https://" />
        </Field>
      </div>

      <div className="form-item">
        <span className="form-label">Thumbnail preview</span>
        <div style={{ maxWidth: 320 }}>
          <Thumb src={previewThumb} variant="wide" />
        </div>
      </div>

      <div className="pk-form__row">
        <Field
          name="category"
          label={
            <>
              Category <span className="pk-optional">(optional)</span>
            </>
          }
          rules={[{ max: 60, message: 'Keep it under 60 characters' }]}
        >
          <input className="pk-input" maxLength={60} placeholder="e.g. Web app" />
        </Field>
        <Field
          name="level"
          label={
            <>
              Level <span className="pk-optional">(optional)</span>
            </>
          }
        >
          <select className="pk-select">
            <option value="">Not set</option>
            {VIDEO_LEVELS.map(level => (
              <option key={level} value={level}>
                {level}
              </option>
            ))}
          </select>
        </Field>
      </div>

      <Field
        name="features"
        label={
          <>
            Key features <span className="pk-optional">(optional)</span>
          </>
        }
        help="One per line. Shown as a checklist under the video."
      >
        <textarea className="pk-textarea" rows={5} placeholder={'Patient registration\nDoctor appointments\nBilling with invoices'} />
      </Field>

      <Field
        name="whatsIncluded"
        label={
          <>
            What the buyer gets <span className="pk-optional">(optional)</span>
          </>
        }
        help="One per line, e.g. full source code, database script, project report."
      >
        <textarea className="pk-textarea" rows={4} placeholder={'Full source code\nDatabase script\nProject report (PDF)'} />
      </Field>

      <Field
        name="chapters"
        label={
          <>
            Chapters <span className="pk-optional">(optional)</span>
          </>
        }
        rules={[chaptersRule]}
        help="One per line: a time, then what's shown. Visitors can jump to each part."
      >
        <textarea className="pk-textarea" rows={5} placeholder={'0:00 Overview\n1:20 Admin dashboard\n4:05 Billing'} />
      </Field>

      <Field name="technologyStack" label="Technologies" help="Press Enter after each one.">
        <TagsInput placeholder="e.g. React" tokenSeparators={[]} />
      </Field>

      <div className="pk-form__row">
        <Field
          name="projectLink"
          label={
            <>
              Live demo link <span className="pk-optional">(optional)</span>
            </>
          }
          rules={[optionalUrlRule('Enter a full URL that starts with https://')]}
        >
          <input className="pk-input" type="url" inputMode="url" placeholder="https://" />
        </Field>
        <Field
          name="githubLink"
          label={
            <>
              GitHub link <span className="pk-optional">(optional)</span>
            </>
          }
          rules={[optionalUrlRule('Enter a full URL that starts with https://')]}
        >
          <input className="pk-input" type="url" inputMode="url" placeholder="https://github.com/" />
        </Field>
      </div>

      <div className="pk-form__row">
        <Field
          name="duration"
          label={
            <>
              Duration in seconds <span className="pk-optional">(optional)</span>
            </>
          }
          rules={[{ type: 'integer', min: 0, message: 'Use a whole number of seconds' }]}
          help={duration > 0 ? `Shown as ${durationLabel(duration)}` : 'Leave blank if you don’t know it.'}
        >
          <input type="number" className="pk-input" min={0} step={1} inputMode="numeric" placeholder="330" />
        </Field>
        <div className="form-item" style={{ alignSelf: 'end' }}>
          <Field name="isPublished" valuePropName="checked" noStyle>
            {field => (
              <label className="pk-check" style={{ minHeight: 42, alignItems: 'center' }}>
                <input type="checkbox" {...field} />
                <span>Published (visible on the Videos page)</span>
              </label>
            )}
          </Field>
        </div>
      </div>

      <div className="adm-form-foot">
        <button type="button" className="pk-btn pk-btn--secondary pk-btn--sm" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="pk-btn pk-btn--primary pk-btn--sm" disabled={saving} aria-busy={saving || undefined}>
          {video ? 'Save changes' : 'Add video'}
        </button>
      </div>
    </Form>
  );
};

// The API only attaches the product when visitors can see it.
const linkedProjectLabel = video => {
  if (video.product) return `${video.product.name} · ${formatINR(video.product.price)}`;
  return video.productId ? 'Linked, but the project isn’t public' : '—';
};

const VideoPreview = ({ video }) => {
  const youtubeId = getYouTubeVideoId(video.videoUrl);
  return (
    <div>
      {youtubeId ? (
        <div className="adm-embed">
          <iframe
            src={`https://www.youtube-nocookie.com/embed/${youtubeId}`}
            title={video.title}
            allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      ) : (
        <p className="pk-alert" data-tone="warning">
          This link isn't a YouTube video, so it can't be previewed here.
        </p>
      )}
      <div className="adm-tags" style={{ marginBottom: 12 }}>
        <span className="pk-badge" data-tone={video.isPublished ? 'success' : undefined}>
          {video.isPublished ? 'Published' : 'Draft'}
        </span>
        <span className="pk-badge">{formatNumber(video.views || 0)} views</span>
        <span className="pk-badge">Duration {durationLabel(video.duration)}</span>
        {video.category && <span className="pk-badge">{video.category}</span>}
        {video.level && <span className="pk-badge">{video.level}</span>}
      </div>
      <p className="adm-message">{video.description}</p>
      {video.features?.length > 0 && (
        <ul style={{ margin: '12px 0 0', paddingLeft: 20 }}>
          {video.features.map(feature => (
            <li key={feature}>{feature}</li>
          ))}
        </ul>
      )}
      {video.technologyStack?.length > 0 && (
        <div className="adm-tags" style={{ marginTop: 12 }}>
          {video.technologyStack.map(tech => (
            <span key={tech} className="adm-tag">
              {tech}
            </span>
          ))}
        </div>
      )}
      <dl className="pk-rows" style={{ marginTop: 16 }}>
        <div>
          <dt>Linked project</dt>
          <dd>{linkedProjectLabel(video)}</dd>
        </div>
        {video.chapters?.length > 0 && (
          <div>
            <dt>Chapters</dt>
            <dd>
              {video.chapters.map(c => `${formatTimestamp(c.start)} ${c.title}`).join(' · ')}
            </dd>
          </div>
        )}
        <div>
          <dt>Added</dt>
          <dd>{formatDate(video.createdAt)}</dd>
        </div>
        <div>
          <dt>Last updated</dt>
          <dd>{formatDate(video.updatedAt || video.createdAt)}</dd>
        </div>
      </dl>
      {(video.projectLink || video.githubLink) && (
        <div className="pk-cluster" style={{ marginTop: 16 }}>
          {video.projectLink && (
            <a className="pk-btn pk-btn--secondary pk-btn--sm" href={video.projectLink} target="_blank" rel="noopener noreferrer">
              <Globe aria-hidden="true" />
              Live demo
            </a>
          )}
          {video.githubLink && (
            <a className="pk-btn pk-btn--secondary pk-btn--sm" href={video.githubLink} target="_blank" rel="noopener noreferrer">
              <Github aria-hidden="true" />
              GitHub
            </a>
          )}
        </div>
      )}
    </div>
  );
};

const VideoManagement = () => {
  const [videos, setVideos] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [stats, setStats] = useState({ total: null, published: null });
  const [statsLoading, setStatsLoading] = useState(true);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const debouncedSearch = useDebouncedValue(search.trim(), 350);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [previewing, setPreviewing] = useState(null);
  const [busyIds, setBusyIds] = useState(() => new Set());

  const hasFilters = Boolean(debouncedSearch || statusFilter);
  const latestRequest = useRef(0);

  const loadVideos = useCallback(async () => {
    const requestId = ++latestRequest.current;
    setLoading(true);
    setError(null);
    try {
      const response = await getAllVideos(page, pageSize, {
        isPublished: statusFilter ? statusFilter === 'published' : undefined,
        searchTerm: debouncedSearch ? escapeRegex(debouncedSearch) : undefined,
      });
      if (requestId !== latestRequest.current) return;
      const data = response?.data || {};
      const items = Array.isArray(data.items) ? data.items : [];
      setVideos(items);
      setTotalItems(Number(data.totalItems) || 0);
      // Deleting the last video on a page leaves it empty; step back a page.
      if (items.length === 0 && page > 1 && Number(data.totalItems) > 0) setPage(p => Math.max(1, p - 1));
    } catch (err) {
      if (requestId === latestRequest.current) setError(getErrorMessage(err, "Couldn't load videos."));
    } finally {
      if (requestId === latestRequest.current) setLoading(false);
    }
  }, [page, pageSize, statusFilter, debouncedSearch]);

  // Totals for every video, not just the current page or filter.
  const loadStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const [all, published] = await Promise.all([
        getAllVideos(1, 1),
        getAllVideos(1, 1, { isPublished: true }),
      ]);
      setStats({
        total: Number(all?.data?.totalItems) || 0,
        published: Number(published?.data?.totalItems) || 0,
      });
    } catch {
      setStats({ total: null, published: null });
    } finally {
      setStatsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadVideos();
  }, [loadVideos]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  // A new search or filter starts from the first page.
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, statusFilter]);

  const refresh = () => {
    loadVideos();
    loadStats();
  };

  const setBusy = (id, busy) =>
    setBusyIds(prev => {
      const next = new Set(prev);
      if (busy) next.add(id);
      else next.delete(id);
      return next;
    });

  const openCreate = () => {
    setEditing(null);
    setModalOpen(true);
  };

  const openEdit = video => {
    setEditing(video);
    setModalOpen(true);
  };

  const handleSaved = () => {
    setModalOpen(false);
    refresh();
  };

  const togglePublished = async video => {
    const next = !video.isPublished;
    setBusy(video.id, true);
    try {
      await updateVideo(video.id, { isPublished: next });
      setVideos(list => list.map(v => (v.id === video.id ? { ...v, isPublished: next } : v)));
      toast.success(next ? 'Video published' : 'Video moved to drafts');
      loadStats();
    } catch (err) {
      toast.error(getErrorMessage(err, "Couldn't change the status."));
    } finally {
      setBusy(video.id, false);
    }
  };

  const handleDelete = async video => {
    try {
      await deleteVideo(video.id);
      toast.success('Video deleted');
      refresh();
    } catch (err) {
      toast.error(getErrorMessage(err, "Couldn't delete the video."));
    }
  };

  const pageViews = videos.reduce((sum, v) => sum + (Number(v.views) || 0), 0);
  const drafts = stats.total !== null && stats.published !== null ? stats.total - stats.published : null;

  const columns = [
    {
      title: 'Video',
      key: 'video',
      render: (_, record) => (
        <div className="adm-cell">
          <Thumb src={record.thumbnailUrl || getYouTubeThumbnail(record.videoUrl)} variant="video" />
          <div className="adm-cell__text">
            <div className="adm-cell-title">{record.title}</div>
            <div className="adm-cell-sub adm-clamp-2">{record.description}</div>
          </div>
        </div>
      ),
    },
    {
      title: 'Technologies',
      dataIndex: 'technologyStack',
      key: 'technologyStack',
      width: 200,
      render: stack =>
        stack?.length ? (
          <div className="adm-tags">
            {stack.slice(0, 3).map(tech => (
              <span key={tech} className="adm-tag">
                {tech}
              </span>
            ))}
            {stack.length > 3 && <span className="adm-tag">+{stack.length - 3}</span>}
          </div>
        ) : (
          <span className="pk-muted">—</span>
        ),
    },
    {
      title: 'Project',
      key: 'product',
      width: 180,
      render: (_, record) =>
        record.product ? (
          <div className="adm-cell__text">
            <div className="adm-clamp-2">{record.product.name}</div>
            <div className="adm-cell-sub">{formatINR(record.product.price)}</div>
          </div>
        ) : (
          <span className="pk-muted">{record.productId ? 'Not public' : '—'}</span>
        ),
    },
    {
      title: 'Length',
      dataIndex: 'duration',
      key: 'duration',
      width: 90,
      render: duration => <span className="adm-num">{durationLabel(duration)}</span>,
    },
    {
      title: 'Views',
      dataIndex: 'views',
      key: 'views',
      width: 90,
      align: 'right',
      render: views => <span className="adm-num">{formatNumber(views || 0)}</span>,
    },
    {
      title: 'Status',
      dataIndex: 'isPublished',
      key: 'isPublished',
      width: 140,
      render: (isPublished, record) => (
        <label className="adm-switch">
          <input
            type="checkbox"
            role="switch"
            className="switch switch-sm"
            checked={Boolean(isPublished)}
            disabled={busyIds.has(record.id)}
            aria-label={`Published: ${record.title}`}
            onChange={() => togglePublished(record)}
          />
          <span>{isPublished ? 'Published' : 'Draft'}</span>
        </label>
      ),
    },
    {
      title: 'Added',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
      render: date => <span className="adm-nowrap">{formatDate(date)}</span>,
    },
    {
      title: <span className="sr-only">Actions</span>,
      key: 'actions',
      width: 130,
      fixed: 'right',
      render: (_, record) => (
        <div className="adm-actions">
          <button
            type="button"
            className="adm-icon-btn"
            aria-label={`Preview ${record.title}`}
            title="Preview"
            onClick={() => setPreviewing(record)}
          >
            <Play aria-hidden="true" />
          </button>
          <button
            type="button"
            className="adm-icon-btn"
            aria-label={`Edit ${record.title}`}
            title="Edit"
            onClick={() => openEdit(record)}
          >
            <Pencil aria-hidden="true" />
          </button>
          <Popconfirm
            title="Delete this video?"
            description="It will disappear from the Videos page. To hide it instead, move it to drafts."
            okText="Delete"
            okDanger
            onConfirm={() => handleDelete(record)}
          >
            <button type="button" className="adm-icon-btn" data-tone="danger" aria-label={`Delete ${record.title}`} title="Delete">
              <Trash2 aria-hidden="true" />
            </button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  let body;
  if (loading && videos.length === 0 && !error) {
    body = <LoadingState label="Loading videos" />;
  } else if (error) {
    body = (
      <div className="adm-panel__state">
        <ErrorState title="Couldn't load videos" message={error} onRetry={refresh} />
      </div>
    );
  } else if (!hasFilters && totalItems === 0) {
    body = (
      <div className="adm-panel__state">
        <EmptyState
          icon={<Clapperboard />}
          title="No videos yet"
          text="Add a YouTube walkthrough of a project. Published videos appear on the Videos page."
          actions={
            <button type="button" className="pk-btn pk-btn--primary pk-btn--sm" onClick={openCreate}>
              <Plus aria-hidden="true" />
              Add video
            </button>
          }
        />
      </div>
    );
  } else {
    body = (
      <Table
        dataSource={videos}
        columns={columns}
        loading={loading}
        rowKey="id"
        scroll={{ x: 1160 }}
        emptyText="No videos match these filters."
        pagination={{
          current: page,
          pageSize,
          total: totalItems,
          showSizeChanger: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
          onChange: (nextPage, nextSize) => {
            if (nextSize !== pageSize) {
              setPageSize(nextSize);
              setPage(1);
            } else {
              setPage(nextPage);
            }
          },
        }}
      />
    );
  }

  return (
    <div>
      <PageHeader
        title="Videos"
        description="Project walkthroughs shown on the Videos page. Drafts stay hidden from visitors."
        actions={
          <button type="button" className="pk-btn pk-btn--primary pk-btn--sm" onClick={openCreate}>
            <Plus aria-hidden="true" />
            Add video
          </button>
        }
      />

      <div className="adm-stats">
        <StatCard title="Videos" value={stats.total ?? '—'} icon={<Clapperboard />} loading={statsLoading} />
        <StatCard title="Published" value={stats.published ?? '—'} icon={<CircleCheck />} loading={statsLoading} />
        <StatCard title="Drafts" value={drafts ?? '—'} icon={<FilePen />} loading={statsLoading} />
        <StatCard title="Views" value={pageViews} icon={<Eye />} hint="Videos on this page" loading={loading && !videos.length} />
      </div>

      <FilterBar
        searchLabel="Search videos"
        searchPlaceholder="Search title or description"
        searchValue={search}
        onSearchChange={setSearch}
        filters={[
          {
            key: 'status',
            component: (
              <select
                className="pk-select"
                aria-label="Filter by status"
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value)}
              >
                <option value="">Published and drafts</option>
                <option value="published">Published</option>
                <option value="draft">Drafts</option>
              </select>
            ),
          },
        ]}
        onClearFilters={
          search || statusFilter
            ? () => {
                setSearch('');
                setStatusFilter('');
              }
            : undefined
        }
      />

      <section className="adm-panel" aria-label="Videos">
        {body}
      </section>

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? 'Edit video' : 'Add video'}
        footer={null}
        width={760}
        className="adm-modal"
        maskClosable={false}
      >
        <VideoForm video={editing} onCancel={() => setModalOpen(false)} onSaved={handleSaved} />
      </Modal>

      <Modal
        open={Boolean(previewing)}
        onClose={() => setPreviewing(null)}
        title={previewing?.title}
        footer={null}
        width={820}
        className="adm-modal"
      >
        {previewing && <VideoPreview video={previewing} />}
      </Modal>
    </div>
  );
};

export default VideoManagement;
